import { ReactNode, useState, useRef, useEffect, useId, useCallback } from 'react';

import { CLICK_OUTSIDE_EVENT } from '../constants/event.constants';

export interface TooltipProps {
    children: ReactNode;
    content: string | ReactNode;
    className?: string;
    position?: 'right' | 'left';
}

/**
 * Shared Tooltip Component
 * 
 * A reusable tooltip that appears on hover/click with consistent styling.
 * Dimensions and alignment are identical across mobile, tablet, and desktop.
 * Supports touch devices and keyboard navigation.
 */
const Tooltip = ({
    children,
    content,
    className = '',
    position = 'right',
}: TooltipProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [overflowOffset, setOverflowOffset] = useState(0);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const id = useId();
    const tooltipId = `tooltip-${id.replace(/:/g, '')}`;

    // Figma-spec positioning offsets (same for all viewports)
    const positionStyles = position === 'left'
        ? { containerClass: 'left-[-12px]', arrowClass: 'left-[14px]' }
        : { containerClass: 'right-[-12px]', arrowClass: 'right-[14px]' };

    // Viewport boundary clamping - prevents tooltip from going off-screen
    const recalcOverflow = useCallback(() => {
        if (!showTooltip || !tooltipRef.current) {
            setOverflowOffset(0);
            return;
        }

        const rect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const margin = 8; // min gap from viewport edge
        let offset = 0;

        if (rect.left < margin) {
            offset = margin - rect.left;
        } else if (rect.right > viewportWidth - margin) {
            offset = (viewportWidth - margin) - rect.right;
        }

        setOverflowOffset(offset); // React skips re-render if value is unchanged — no manual guard needed
    }, [showTooltip]);

    useEffect(() => {
        recalcOverflow();
    }, [showTooltip, recalcOverflow]);

    // Close tooltip on outside click (for mobile tap-to-open)
    useEffect(() => {
        if (!showTooltip) return;

        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowTooltip(false);
            }
        };

        document.addEventListener(CLICK_OUTSIDE_EVENT, handleOutsideClick);
        return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, handleOutsideClick);
    }, [showTooltip]);

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (e.type === 'click' || (e as React.KeyboardEvent).key === 'Enter' || (e as React.KeyboardEvent).key === ' ') {
            if (e.type === 'keydown') e.preventDefault();
            setShowTooltip(prev => !prev);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setShowTooltip(false);
            return;
        }
        handleToggle(e);
    };

    // Only allow hover on devices that actually support it
    const handleMouseEnter = () => {
        if (typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches) {
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        if (typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches) {
            setShowTooltip(false);
        }
    };

    return (
        <div
            ref={containerRef}
            className={`relative inline-flex items-center ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={showTooltip}
                aria-describedby={showTooltip ? tooltipId : undefined}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                className="inline-flex items-center cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded-full"
            >
                {children}
            </div>

            {showTooltip && (
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    className={`absolute bottom-full mb-[12px] ${positionStyles.containerClass} w-max max-w-[calc(100vw-32px)] px-[12px] py-[8px] rounded-[8px] shadow-lg z-[100] pointer-events-none flex flex-col items-start font-semibold text-[12px] leading-[18px] tracking-[0] whitespace-pre-line`}
                    style={{
                        fontFamily: 'Inter, sans-serif',
                        gap: '4px',
                        background: '#101828',
                        color: '#FFF',
                        ...(overflowOffset !== 0 && {
                            transform: `translateX(${overflowOffset}px)`,
                        }),
                    }}
                >
                    {content}
                    {/* Diamond arrow pointer - stays fixed relative to trigger */}
                    <div 
                        className={`absolute top-full ${positionStyles.arrowClass} w-[12px] h-[12px] -mt-[8px] rounded-[1px]`}
                        style={{ 
                            background: '#101828',
                            transform: `translateX(${-overflowOffset}px) rotate(45deg)`,
                        }}
                    ></div>
                </div>
            )}
        </div>
    );
};

export default Tooltip;
